"use strict";
/**
 *
 * hooks
 *
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const resize_observer_polyfill_1 = __importDefault(require("resize-observer-polyfill"));
const fast_equals_1 = require("fast-equals");
function useForceUpdate() {
    const [, setCounter] = react_1.useState(0);
    const forceUpdate = react_1.useCallback(() => setCounter((counter) => counter + 1), []);
    return forceUpdate;
}
function useSize() {
    const forceUpdate = useForceUpdate();
    // reference used on the component which size wants to be tracked
    const [refEl, setRefEl] = react_1.useState(null);
    // size state, updated on resize
    const [size, setSize] = react_1.useState({ width: null, height: null });
    // create the observer
    const observer = react_1.useMemo(() => new resize_observer_polyfill_1.default((entries) => {
        const { width, height } = entries[0].contentRect;
        // TODO-db-190408 throttle the `setState`
        setSize((currSize) => currSize.width !== width || currSize.height !== height ? { width, height } : currSize);
    }), []);
    // observe the referenced component
    react_1.useEffect(() => {
        if (!refEl) {
            return;
        }
        observer.observe(refEl);
        window.setTimeout(forceUpdate, 0);
        return () => {
            observer.unobserve(refEl);
        };
    }, [refEl]);
    return [setRefEl, size];
}
exports.useSize = useSize;
function setRef(ref, value) {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore because the `current` object is flagged as readonly
        ref.current = value;
    }
}
exports.setRef = setRef;
function useForkRef(refA, refB) {
    /**
     * This will create a new function if the ref props change and are defined.
     * This means react will call the old forkRef with `null` and the new forkRef
     * with the ref. Cleanup naturally emerges from this behavior
     */
    return react_1.useMemo(() => {
        if (refA == null && refB == null) {
            return null;
        }
        return (refValue) => {
            setRef(refA, refValue);
            setRef(refB, refValue);
        };
    }, [refA, refB]);
}
exports.useForkRef = useForkRef;
function useMemoRenderer(props, render, propsAreEqual = fast_equals_1.shallowEqual) {
    const propsRef = react_1.useRef();
    const elementRef = react_1.useRef();
    if (!propsRef.current || !propsAreEqual(propsRef.current, props)) {
        elementRef.current = render(props);
    }
    propsRef.current = props;
    return elementRef.current;
}
exports.useMemoRenderer = useMemoRenderer;
